/**
 * ConstructionProposalBuilder
 * --------------------------
 * Full-page, collapsible-section builder for healthcare construction
 * proposals: project header, project-manager block, project org structure,
 * CSI-division line items, trade-partner licences/certifications (with file
 * upload), closeout requirements, payment milestones and signature.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Save, Eye, ArrowLeft, ChevronDown, ChevronRight,
  Maximize2, Minimize2, Upload, FileText, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "header", label: "Project Header & Owner" },
  { id: "scope", label: "Scope, Inclusions, Exclusions & General Conditions" },
  { id: "pm", label: "Project Manager" },
  { id: "team", label: "Project Organizational Structure" },
  { id: "divisions", label: "Scope of Work & Pricing by CSI Division" },
  { id: "contractors", label: "Trade Partners — Licences & Certifications" },
  { id: "closeout", label: "Documentation & Closeout Requirements" },
  { id: "payment", label: "Payment Schedule, Warranty & Signature" },
];

const empty = () => ({
  proposal_number: "", status: "draft", project_name: "", job_number: "",
  owner_name: "", facility: "", location: "", issue_date: new Date().toISOString().slice(0, 10),
  valid_days: 30, target_complete: "",
  prime_contractor_line: "<strong>Prime Contractor:</strong> Sterile Mate LLC · HUB &amp; MBE Certified",
  kicker: "Healthcare Construction Proposal",
  executive_summary: "", scope_description: "",
  pm_name: "", pm_company: "", pm_title: "Project Manager", pm_email: "", pm_phone: "",
  pm_responsibilities: [], team: [], divisions: [{ code: "DIV 01", title: "General Requirements", responsible: "", items: [] }],
  contractors: [], inclusions: [], exclusions: [], general_conditions: [], closeout_requirements: [],
  contingency_percent: 0, payment_schedule: [], payment_terms_note: "", warranty_note: "",
  limitation_of_liability: "", signer_name: "George Broughton III",
  signer_title: "Founder & CEO · Sterile Mate LLC", signer_date: "",
});

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const uid = () => Math.random().toString(16).slice(2, 12);

export default function ConstructionProposalBuilder({ proposalId, token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [p, setP] = useState(empty());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wide, setWide] = useState(false);
  const [open, setOpen] = useState(() => new Set(SECTIONS.map((s) => s.id)));
  const isNew = !proposalId || proposalId === "new";

  useEffect(() => {
    if (isNew) { setP(empty()); return; }
    setLoading(true);
    axios.get(`${API}/api/admin/construction-proposals/${proposalId}`, { headers })
      .then((r) => setP(r.data))
      .catch(() => toast.error("Failed to load proposal"))
      .finally(() => setLoading(false));
  }, [proposalId, isNew, headers]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const toggle = (id) => setOpen((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const totals = useMemo(() => {
    const sub = (p.divisions || []).reduce((s, d) =>
      s + (d.items || []).reduce((ds, li) =>
        ds + (Number(li.extended) || Number(li.qty || 0) * Number(li.unit_price || 0)), 0), 0);
    const cont = sub * Number(p.contingency_percent || 0) / 100;
    return { sub, cont, total: sub + cont };
  }, [p.divisions, p.contingency_percent]);

  const save = async () => {
    if (!p.project_name?.trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/admin/construction-proposals`, { ...p, id: isNew ? null : proposalId }, { headers });
      toast.success("Construction proposal saved");
      if (isNew && r.data?.id) window.location.href = `/admin/proposals/construction/${r.data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  // ---- list helpers -------------------------------------------------------
  const listAdd = (k) => set(k, [...(p[k] || []), ""]);
  const listSet = (k, i, v) => set(k, p[k].map((x, idx) => (idx === i ? v : x)));
  const listDel = (k, i) => set(k, p[k].filter((_, idx) => idx !== i));

  const divSet = (di, key, v) => set("divisions", p.divisions.map((d, i) => (i === di ? { ...d, [key]: v } : d)));
  const itemSet = (di, ii, key, v) => set("divisions", p.divisions.map((d, i) => i !== di ? d : {
    ...d,
    items: d.items.map((li, j) => {
      if (j !== ii) return li;
      const next = { ...li, [key]: v };
      if (key === "qty" || key === "unit_price") {
        next.extended = Number((Number(next.qty || 0) * Number(next.unit_price || 0)).toFixed(2));
      }
      return next;
    }),
  }));

  const uploadDoc = useCallback(async (contractorId, file, kind) => {
    if (isNew) { toast.error("Save the proposal first, then upload documents."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(
        `${API}/api/admin/construction-proposals/${proposalId}/contractors/${contractorId}/documents?kind=${kind}`,
        fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      setP((prev) => ({
        ...prev,
        contractors: prev.contractors.map((c) => c.id === contractorId
          ? { ...c, documents: [...(c.documents || []), r.data.document] } : c),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  }, [proposalId, isNew, headers]);

  const delDoc = async (contractorId, docId) => {
    try {
      await axios.delete(`${API}/api/admin/construction-proposals/${proposalId}/contractors/${contractorId}/documents/${docId}`, { headers });
      setP((prev) => ({
        ...prev,
        contractors: prev.contractors.map((c) => c.id === contractorId
          ? { ...c, documents: (c.documents || []).filter((d) => d.id !== docId) } : c),
      }));
    } catch { toast.error("Could not remove document"); }
  };

  return (
    <div className="w-full" data-testid="construction-proposal-builder">
      <div className={`bg-white rounded-xl border border-slate-200 shadow-sm mx-auto ${wide ? "max-w-none" : "max-w-6xl"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-200 px-4 sm:px-5 py-3 bg-slate-50 rounded-t-xl sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { window.location.href = "/admin/proposals"; }} data-testid="cpb-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-amber-700">Healthcare Construction Proposal</p>
              <h2 className="text-base sm:text-lg font-extrabold text-slate-900">{isNew ? "New Construction Proposal" : p.project_name || "Edit Proposal"}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="text-right">
              <p className="text-[10px] text-slate-500">Total Proposed Price</p>
              <p className="text-lg font-black text-slate-900" data-testid="cpb-total">{fmt$(totals.total)}</p>
            </div>
            {!isNew && (
              <>
                <a href={`${API}/api/construction-proposals/${proposalId}/preview`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md ring-1 ring-slate-300 text-xs font-bold text-slate-700 hover:ring-cyan-500"
                   data-testid="cpb-preview"><Eye className="w-3.5 h-3.5" /> Preview</a>
                <a href={`${API}/api/construction-proposals/${proposalId}/pdf`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black"
                   data-testid="cpb-pdf"><Download className="w-3.5 h-3.5" /> PDF</a>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => setWide((w) => !w)} data-testid="cpb-width-toggle">
              {wide ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button onClick={save} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="cpb-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-1" />Save</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">{open.size} of {SECTIONS.length} sections open</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(new Set(SECTIONS.map((s) => s.id)))} data-testid="cpb-expand-all">Expand all</Button>
                <Button variant="outline" size="sm" onClick={() => setOpen(new Set())} data-testid="cpb-collapse-all">Collapse all</Button>
              </div>
            </div>

            <Section id="header" title={SECTIONS[0].label} open={open.has("header")} onToggle={toggle}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F l="Proposal No."><Input value={p.proposal_number} onChange={(e) => set("proposal_number", e.target.value)} placeholder="SM-HH-LBJ-2026-001" data-testid="cpb-number" /></F>
                <F l="Status"><select className="w-full h-10 border rounded px-3 text-sm" value={p.status} onChange={(e) => set("status", e.target.value)}><option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option><option value="declined">Declined</option></select></F>
                <F l="Project name *"><Input value={p.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="LBJ Isolation Nursery Renovation" data-testid="cpb-project-name" /></F>
                <F l="Owner job number"><Input value={p.job_number} onChange={(e) => set("job_number", e.target.value)} placeholder="Harris Health JOB NO. 26/0249" /></F>
                <F l="Owner"><Input value={p.owner_name} onChange={(e) => set("owner_name", e.target.value)} placeholder="Harris Health System" /></F>
                <F l="Facility"><Input value={p.facility} onChange={(e) => set("facility", e.target.value)} placeholder="LBJ Hospital — Well Baby Nursery" /></F>
                <F l="Location"><Input value={p.location} onChange={(e) => set("location", e.target.value)} placeholder="Houston, TX" /></F>
                <F l="Target completion"><Input value={p.target_complete} onChange={(e) => set("target_complete", e.target.value)} placeholder="Q3 2027" /></F>
                <F l="Issue date"><Input type="date" value={p.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></F>
                <F l="Valid (days)"><Input type="number" value={p.valid_days} onChange={(e) => set("valid_days", Number(e.target.value))} /></F>
                <F l="Prime contractor line" className="sm:col-span-2"><Input value={p.prime_contractor_line} onChange={(e) => set("prime_contractor_line", e.target.value)} /></F>
              </div>
            </Section>

            <Section id="scope" title={SECTIONS[1].label} open={open.has("scope")} onToggle={toggle}>
              <F l="Project understanding / executive summary (HTML allowed)"><Textarea rows={5} value={p.executive_summary} onChange={(e) => set("executive_summary", e.target.value)} data-testid="cpb-summary" /></F>
              <F l="Scope description"><Textarea rows={4} value={p.scope_description} onChange={(e) => set("scope_description", e.target.value)} /></F>
              <StrList label="Inclusions" k="inclusions" p={p} add={listAdd} upd={listSet} del={listDel} />
              <StrList label="Exclusions" k="exclusions" p={p} add={listAdd} upd={listSet} del={listDel} />
              <StrList label="General conditions" k="general_conditions" p={p} add={listAdd} upd={listSet} del={listDel} />
            </Section>

            <Section id="pm" title={SECTIONS[2].label} open={open.has("pm")} onToggle={toggle}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F l="Project manager name"><Input value={p.pm_name} onChange={(e) => set("pm_name", e.target.value)} placeholder="Donna Winfrey" data-testid="cpb-pm-name" /></F>
                <F l="Company"><Input value={p.pm_company} onChange={(e) => set("pm_company", e.target.value)} placeholder="Winfrey Development" /></F>
                <F l="Title"><Input value={p.pm_title} onChange={(e) => set("pm_title", e.target.value)} /></F>
                <F l="Email"><Input value={p.pm_email} onChange={(e) => set("pm_email", e.target.value)} /></F>
                <F l="Phone"><Input value={p.pm_phone} onChange={(e) => set("pm_phone", e.target.value)} /></F>
              </div>
              <StrList label="PM responsibilities" k="pm_responsibilities" p={p} add={listAdd} upd={listSet} del={listDel} />
            </Section>

            <Section id="team" title={SECTIONS[3].label} open={open.has("team")} onToggle={toggle}>
              {(p.team || []).map((m, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-start" data-testid={`cpb-team-${i}`}>
                  <Input className="sm:col-span-2" placeholder="Name" value={m.name || ""} onChange={(e) => set("team", p.team.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <Input className="sm:col-span-2" placeholder="Company" value={m.company || ""} onChange={(e) => set("team", p.team.map((x, j) => j === i ? { ...x, company: e.target.value } : x))} />
                  <Input className="sm:col-span-3" placeholder="Role" value={m.role || ""} onChange={(e) => set("team", p.team.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} />
                  <Input className="sm:col-span-2" placeholder="Reports to" value={m.reports_to || ""} onChange={(e) => set("team", p.team.map((x, j) => j === i ? { ...x, reports_to: e.target.value } : x))} />
                  <Input className="sm:col-span-2" placeholder="Email / phone" value={m.email || ""} onChange={(e) => set("team", p.team.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
                  <Button size="icon" variant="ghost" className="sm:col-span-1" onClick={() => set("team", p.team.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => set("team", [...(p.team || []), { name: "", company: "", role: "", reports_to: "", email: "" }])} data-testid="cpb-team-add"><Plus className="w-3 h-3 mr-1" />Add team member</Button>
            </Section>

            <Section id="divisions" title={SECTIONS[4].label} open={open.has("divisions")} onToggle={toggle}>
              {(p.divisions || []).map((d, di) => {
                const dTotal = (d.items || []).reduce((s, li) => s + (Number(li.extended) || 0), 0);
                return (
                  <div key={di} className="rounded-lg border border-slate-200 mb-3" data-testid={`cpb-div-${di}`}>
                    <div className="flex flex-wrap gap-2 items-center bg-slate-50 p-2 border-b border-slate-200">
                      <Input className="w-28" placeholder="DIV 22" value={d.code || ""} onChange={(e) => divSet(di, "code", e.target.value)} />
                      <Input className="flex-1 min-w-[160px]" placeholder="Plumbing & Medical Gas" value={d.title || ""} onChange={(e) => divSet(di, "title", e.target.value)} />
                      <Input className="flex-1 min-w-[160px]" placeholder="Responsible trade partner" value={d.responsible || ""} onChange={(e) => divSet(di, "responsible", e.target.value)} />
                      <span className="text-sm font-black text-slate-800">{fmt$(dTotal)}</span>
                      <Button size="icon" variant="ghost" onClick={() => set("divisions", p.divisions.filter((_, j) => j !== di))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                    <div className="p-2">
                      {(d.items || []).map((li, ii) => (
                        <div key={ii} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-1.5 items-center">
                          <Input className="sm:col-span-6" placeholder="Description of work" value={li.description || ""} onChange={(e) => itemSet(di, ii, "description", e.target.value)} />
                          <Input className="sm:col-span-1" type="number" placeholder="Qty" value={li.qty ?? ""} onChange={(e) => itemSet(di, ii, "qty", Number(e.target.value))} />
                          <Input className="sm:col-span-1" placeholder="UOM" value={li.uom || ""} onChange={(e) => itemSet(di, ii, "uom", e.target.value)} />
                          <Input className="sm:col-span-2" type="number" placeholder="Unit $" value={li.unit_price ?? ""} onChange={(e) => itemSet(di, ii, "unit_price", Number(e.target.value))} />
                          <span className="sm:col-span-1 text-xs font-bold text-slate-700 text-right">{fmt$(li.extended)}</span>
                          <Button size="icon" variant="ghost" className="sm:col-span-1" onClick={() => set("divisions", p.divisions.map((x, j) => j === di ? { ...x, items: x.items.filter((_, k) => k !== ii) } : x))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={() => set("divisions", p.divisions.map((x, j) => j === di ? { ...x, items: [...(x.items || []), { description: "", qty: 1, uom: "LS", unit_price: 0, extended: 0 }] } : x))} data-testid={`cpb-item-add-${di}`}><Plus className="w-3 h-3 mr-1" />Add line item</Button>
                    </div>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" variant="outline" onClick={() => set("divisions", [...(p.divisions || []), { code: "", title: "", responsible: "", items: [] }])} data-testid="cpb-div-add"><Plus className="w-3 h-3 mr-1" />Add CSI division</Button>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-600">Contingency %</span>
                  <Input type="number" className="w-20 h-8" value={p.contingency_percent ?? 0} onChange={(e) => set("contingency_percent", Number(e.target.value))} />
                </div>
                <span className="text-sm font-black text-slate-900 ml-auto">Subtotal {fmt$(totals.sub)} · Total {fmt$(totals.total)}</span>
              </div>
            </Section>

            <Section id="contractors" title={SECTIONS[5].label} open={open.has("contractors")} onToggle={toggle}>
              {isNew && <p className="text-xs text-amber-700 font-semibold">Save the proposal first to enable licence &amp; certification uploads.</p>}
              {(p.contractors || []).map((c, ci) => (
                <div key={c.id || ci} className="rounded-lg border border-slate-200 p-3 mb-3" data-testid={`cpb-contractor-${ci}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                    <Input placeholder="Company" value={c.company || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, company: e.target.value } : x))} />
                    <Input placeholder="Trade" value={c.trade || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, trade: e.target.value } : x))} />
                    <Input placeholder="HUB / MBE / SWBE status" value={c.hub_mbe_status || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, hub_mbe_status: e.target.value } : x))} />
                    <Input placeholder="Contact name" value={c.contact_name || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, contact_name: e.target.value } : x))} />
                    <Input placeholder="Email" value={c.email || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, email: e.target.value } : x))} />
                    <Input placeholder="Phone" value={c.phone || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, phone: e.target.value } : x))} />
                    <Input placeholder="Licence type (e.g. TX Master Plumber)" value={c.license_type || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, license_type: e.target.value } : x))} />
                    <Input placeholder="Licence number" value={c.license_number || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, license_number: e.target.value } : x))} />
                    <div className="grid grid-cols-2 gap-2">
                      <F l="Licence expires"><Input type="date" value={c.license_expires || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, license_expires: e.target.value } : x))} /></F>
                      <F l="Insurance expires"><Input type="date" value={c.insurance_expires || ""} onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, insurance_expires: e.target.value } : x))} /></F>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end gap-2 mb-2 border-t border-dashed border-slate-200 pt-2">
                    <F l="Portal access — contractor user email" className="flex-1 min-w-[220px]">
                      <Input placeholder="electrician@tejaselectric.com" value={c.user_email || ""}
                        onChange={(e) => set("contractors", p.contractors.map((x, j) => j === ci ? { ...x, user_email: e.target.value } : x))}
                        data-testid={`cpb-assign-email-${ci}`} />
                    </F>
                    <div className="flex-1 min-w-[240px]">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-600 block mb-1">Divisions they may price</span>
                      <div className="flex flex-wrap gap-1.5">
                        {(p.divisions || []).filter((d) => d.code).map((d) => {
                          const on = (c.allowed_division_codes || []).includes(d.code);
                          return (
                            <button key={d.code} type="button"
                              onClick={() => set("contractors", p.contractors.map((x, j) => j === ci ? {
                                ...x,
                                allowed_division_codes: on
                                  ? (x.allowed_division_codes || []).filter((z) => z !== d.code)
                                  : [...(x.allowed_division_codes || []), d.code],
                              } : x))}
                              className={`text-[11px] font-bold px-2 py-1 rounded-full ring-1 transition-colors ${on ? "bg-amber-500 text-slate-900 ring-amber-600" : "bg-white text-slate-600 ring-slate-300 hover:ring-amber-500"}`}
                              data-testid={`cpb-scope-${ci}-${d.code.replace(/\s/g, "")}`}>
                              {d.code}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" disabled={isNew}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/api/admin/construction-proposals/${proposalId}/contractors/${c.id}/assign`,
                            { user_email: c.user_email || "", allowed_division_codes: c.allowed_division_codes || [] }, { headers });
                          toast.success(c.user_email ? `${c.user_email} can now price ${(c.allowed_division_codes || []).join(", ") || "nothing yet"}` : "Portal access removed");
                        } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
                      }}
                      data-testid={`cpb-assign-btn-${ci}`}>
                      Assign to job
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {(c.documents || []).map((d) => (
                      <span key={d.id} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 ring-1 ring-slate-300 rounded-full px-2 py-1">
                        <FileText className="w-3 h-3" />
                        <a href={`${API}${d.url}`} target="_blank" rel="noreferrer" className="hover:underline">{d.kind}: {d.name}</a>
                        <button onClick={() => delDoc(c.id, d.id)} className="text-red-600 ml-1">×</button>
                      </span>
                    ))}
                    {["license", "insurance", "certification"].map((kind) => (
                      <label key={kind} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md ring-1 ring-slate-300 cursor-pointer hover:ring-amber-500 ${isNew ? "opacity-50 pointer-events-none" : ""}`} data-testid={`cpb-upload-${ci}-${kind}`}>
                        <Upload className="w-3 h-3" /> Upload {kind}
                        <input type="file" className="hidden" accept="application/pdf,image/*"
                               onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(c.id, f, kind); e.target.value = ""; }} />
                      </label>
                    ))}
                    <Button size="icon" variant="ghost" className="ml-auto" onClick={() => set("contractors", p.contractors.filter((_, j) => j !== ci))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => set("contractors", [...(p.contractors || []), { id: uid(), company: "", trade: "", documents: [] }])} data-testid="cpb-contractor-add"><Plus className="w-3 h-3 mr-1" />Add trade partner</Button>
            </Section>

            <Section id="closeout" title={SECTIONS[6].label} open={open.has("closeout")} onToggle={toggle}>
              <StrList label="Closeout requirements (Div 01 §01 7000 / §01 7800)" k="closeout_requirements" p={p} add={listAdd} upd={listSet} del={listDel} />
            </Section>

            <Section id="payment" title={SECTIONS[7].label} open={open.has("payment")} onToggle={toggle}>
              {(p.payment_schedule || []).map((m, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2">
                  <Input className="sm:col-span-3" placeholder="Milestone" value={m.label || ""} onChange={(e) => set("payment_schedule", p.payment_schedule.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                  <Input className="sm:col-span-1" type="number" placeholder="%" value={m.percent ?? ""} onChange={(e) => set("payment_schedule", p.payment_schedule.map((x, j) => j === i ? { ...x, percent: Number(e.target.value), amount: Number((totals.total * Number(e.target.value) / 100).toFixed(2)) } : x))} />
                  <Input className="sm:col-span-2" type="number" placeholder="Amount" value={m.amount ?? ""} onChange={(e) => set("payment_schedule", p.payment_schedule.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) } : x))} />
                  <Input className="sm:col-span-5" placeholder="When / condition" value={m.when || ""} onChange={(e) => set("payment_schedule", p.payment_schedule.map((x, j) => j === i ? { ...x, when: e.target.value } : x))} />
                  <Button size="icon" variant="ghost" className="sm:col-span-1" onClick={() => set("payment_schedule", p.payment_schedule.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => set("payment_schedule", [...(p.payment_schedule || []), { label: "", percent: null, amount: 0, when: "" }])} data-testid="cpb-pay-add"><Plus className="w-3 h-3 mr-1" />Add milestone</Button>
              <F l="Payment terms note"><Textarea rows={3} value={p.payment_terms_note} onChange={(e) => set("payment_terms_note", e.target.value)} /></F>
              <F l="Warranty"><Textarea rows={3} value={p.warranty_note} onChange={(e) => set("warranty_note", e.target.value)} /></F>
              <F l="Limitation of liability"><Textarea rows={3} value={p.limitation_of_liability} onChange={(e) => set("limitation_of_liability", e.target.value)} /></F>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <F l="Signer name"><Input value={p.signer_name} onChange={(e) => set("signer_name", e.target.value)} /></F>
                <F l="Signer title"><Input value={p.signer_title} onChange={(e) => set("signer_title", e.target.value)} /></F>
                <F l="Signature date"><Input type="date" value={p.signer_date || ""} onChange={(e) => set("signer_date", e.target.value)} /></F>
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ id, title, open, onToggle, children }) {
  return (
    <div className="rounded-lg border border-slate-200 mb-3 overflow-hidden" data-testid={`cpb-section-${id}`}>
      <button type="button" onClick={() => onToggle(id)} className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left" data-testid={`cpb-section-toggle-${id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <span className="text-sm font-bold text-slate-800">{title}</span>
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function F({ l, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] font-black uppercase tracking-wider text-slate-600 block mb-0.5">{l}</span>
      {children}
    </label>
  );
}

function StrList({ label, k, p, add, upd, del: remove }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <p className="text-xs font-black uppercase tracking-wider text-slate-600">{label}</p>
        <Button size="sm" variant="outline" onClick={() => add(k)} data-testid={`cpb-${k}-add`}><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
      {(p[k] || []).map((v, i) => (
        <div key={i} className="flex gap-2 mb-1">
          <Input value={v} onChange={(e) => upd(k, i, e.target.value)} />
          <Button size="icon" variant="ghost" onClick={() => remove(k, i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
        </div>
      ))}
    </div>
  );
}
